import { DataGrid } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import _ from "lodash";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

export default function FilesTable({
  fileUploadList,
  selectedFileIds,
  setSelectedFileIds,
  gridNomenclatureTemplate,
  progressTemplate,
  gridNameTemplate,
  gridCustomFieldTemplate,
  template,
  gridUploadActionTemplate,
  fileNomenclature,
}) {
  const questionFields = template?.flatMap((section) => section.questionFields) || [];
  const { t } = useTranslation();

  const tailwindCenterClass = "flex justify-center items-center";

  const columns = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      sortable: false,
      align: "left",
      filterable: false,
      headerAlign: "left",
      cellClassName: tailwindCenterClass,
      headerClassName: tailwindCenterClass,
      renderCell: (params) => (params.row.isBulkEdit ? null : gridNameTemplate(params.row, params.row.id)),
    },
    {
      field: "progress",
      headerName: "Progress",
      flex: 1,
      sortable: false,
      align: "left",
      filterable: false,
      headerAlign: "left",
      cellClassName: tailwindCenterClass,
      headerClassName: tailwindCenterClass,
      renderCell: (params) => (params.row.isBulkEdit ? null : progressTemplate(params.row, params.row.id)),
    },
  ];

  const hasNomenclatureColumn = fileNomenclature?.fields?.length > 0 && fileUploadList.some((file) => !file.isFolder);

  if (hasNomenclatureColumn) {
    columns.push({
      field: "nomenclature",
      headerName: "Nomenclature",
      flex: 1,
      sortable: false,
      align: "left",
      filterable: false,
      headerAlign: "left",
      cellClassName: tailwindCenterClass,
      headerClassName: tailwindCenterClass,
      renderCell: (params) => (params.row.isBulkEdit ? null : gridNomenclatureTemplate(params.row, params.row.id)),
    });
  }

  const handleCellKeyDown = (event) => {
    if (event.key == " ") {
      event.stopPropagation();
    }
  };

  questionFields.forEach((field) => {
    const isMandatory = field.isMandatory;
    const label = (
      <span>
        {field.questionValue}
        {isMandatory && <span className="text-red-500"> *</span>}
      </span>
    );

    columns.push({
      field: field._id,
      headerName: label,
      flex: 1,
      sortable: false,
      filterable: false,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => (
        <div onKeyDown={handleCellKeyDown} role="gridcell" tabIndex={0}>
          {gridCustomFieldTemplate(field, params.row.id)}
        </div>
      ),
    });
  });

  const hasActionsColumn = fileUploadList.some((file) => !!gridUploadActionTemplate(file, file.id));

  if (hasActionsColumn) {
    columns.push({
      field: "actions",
      headerName: "Actions",
      minWidth: 300,
      flex: 2,
      sortable: false,
      align: "left",
      filterable: false,
      headerAlign: "left",
      cellClassName: tailwindCenterClass,
      headerClassName: tailwindCenterClass,
      renderCell: (params) =>
        params.row.isBulkEdit ? null : (
          <div className="flex justify-center items-center w-full h-full px-2">
            <div className="flex flex-nowrap gap-2 overflow-x-auto w-full">
              {gridUploadActionTemplate(params.row, params.row.id)}
            </div>
          </div>
        ),
    });
  }

  const handleRowSelectionChange = (selectionModel) => {
    const idsArray = Array.isArray(selectionModel) ? selectionModel : Array.from(_.get(selectionModel, "ids", []));
    setSelectedFileIds(idsArray);
  };

  function renderFooter({ selectedCount, totalCount, t }) {
    return (
      <div className="flex justify-between items-center px-4 py-2 text-sm text-gray-600 border-t border-blue-100 w-full">
        <div>
          {selectedCount} {t("Items Selected")}
        </div>
        <div>
          {totalCount} {t("items")}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full">
      <Box className="h-full w-full">
        <DataGrid
          rows={fileUploadList}
          columns={columns}
          checkboxSelection
          disableRowSelectionOnClick
          isRowSelectable={(params) => params.row.id !== 0}
          getRowId={(row) => row.id}
          onRowSelectionModelChange={handleRowSelectionChange}
          disableColumnMenu
          rowHeight={80}
          sx={{
            border: "1px solid #E5EBF3",
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "#E5EBF3",
              justifyContent: "flex-start",
              alignItems: "center",
              display: "flex",
              textAlign: "left",
              paddingLeft: "16px",
            },
            "& .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
              py: "0 !important",
              lineHeight: "32px !important",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold !important",
              justifyContent: "flex-start",
              textAlign: "left",
              width: "100%",
            },
            "& .MuiFormControl-root": { height: "auto !important" },
            "& .MuiDataGrid-row": {
              alignItems: "center",
              borderBottom: "1px solid #E5EBF3",
            },
            "& .MuiCheckbox-root": { margin: "auto" },
            "& .MuiDataGrid-footerContainer": { borderTop: "1px solid #E5EBF3" },
            "& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover": {
              backgroundColor: "#D4E8FA !important",
            },
          }}
          slots={{
            baseCheckbox: CheckBoxComponent,
            footer: renderFooter,
          }}
          slotProps={{
            footer: {
              selectedCount: selectedFileIds.length,
              totalCount: fileUploadList.length,
              t,
            },
          }}
        />
      </Box>
    </div>
  );
}

FilesTable.propTypes = {
  fileUploadList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string,
      isBulkEdit: PropTypes.bool,
    }),
  ),
  selectedFileIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  setSelectedFileIds: PropTypes.func.isRequired,
  gridNomenclatureTemplate: PropTypes.func.isRequired,
  progressTemplate: PropTypes.func.isRequired,
  gridNameTemplate: PropTypes.func.isRequired,
  gridCustomFieldTemplate: PropTypes.func.isRequired,
  gridUploadActionTemplate: PropTypes.func.isRequired,
  template: PropTypes.arrayOf(
    PropTypes.shape({
      questionFields: PropTypes.arrayOf(
        PropTypes.shape({
          _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
          questionValue: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
          isMandatory: PropTypes.bool,
        }),
      ),
    }),
  ),
  fileNomenclature: PropTypes.shape({
    fields: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string,
      }),
    ),
    separator: PropTypes.string,
  }),
};

FilesTable.defaultProps = {
  fileUploadList: [],
  template: [],
  fileNomenclature: { fields: [], separator: "-" },
};
